import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { API_ENDPOINTS } from '../config';

interface AddRecipeSheetProps {
  isOpen: boolean;
  onClose: () => void;
  groceryCollectionId: number;
  token: string;
  onRecipeAdded: () => void;
}

type SheetTab = 'scan' | 'online';

interface ScanResult {
  id: number;
  name: string;
  quantity?: string | number;
  unit?: string;
  selected: boolean;
  categoryIds?: number[];
}

interface OnlineRecipeResult {
  id: number;
  title: string;
  image?: string;
  used_ingredient_count: number;
  missed_ingredient_count: number;
  used_ingredients: string[];
  missed_ingredients: string[];
  likes: number;
  ready_in_minutes?: number;
  servings?: number;
  source_url?: string;
  is_in_library?: boolean;
  library_recipe_id?: number | null;
  is_checked_out?: boolean;
}

const AddRecipeSheet: React.FC<AddRecipeSheetProps> = ({
  isOpen,
  onClose,
  groceryCollectionId,
  token,
  onRecipeAdded,
}) => {
  const [activeTab, setActiveTab] = useState<SheetTab>('scan');
  const [isScanning, setIsScanning] = useState(false);
  const [scanPreview, setScanPreview] = useState<string | null>(null);
  const [scanResults, setScanResults] = useState<ScanResult[]>([]);
  const [scanError, setScanError] = useState<string | null>(null);
  const [recipeData, setRecipeData] = useState<{ recipe_name: string; instructions: string } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const scanInputRef = useRef<HTMLInputElement>(null);

  // Online search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchIngredients, setSearchIngredients] = useState<string[]>([]);
  const [ingredientInput, setIngredientInput] = useState('');
  const [onHandOnly, setOnHandOnly] = useState(false);
  const [minRating, setMinRating] = useState<0 | 3 | 4>(0);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<OnlineRecipeResult[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [savingRecipeId, setSavingRecipeId] = useState<number | null>(null);
  const [savedRecipeIds, setSavedRecipeIds] = useState<Set<number>>(new Set());

  const resetScanState = () => {
    setScanPreview(null);
    setScanResults([]);
    setScanError(null);
    setIsScanning(false);
    setRecipeData(null);
    if (scanInputRef.current) scanInputRef.current.value = '';
  };

  const handleClose = () => {
    resetScanState();
    onClose();
  };

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setScanError(null);
    setIsScanning(true);

    const reader = new FileReader();
    reader.onload = (ev) => setScanPreview(ev.target?.result as string);
    reader.readAsDataURL(file);

    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('mode', 'recipe');

      const response = await fetch(API_ENDPOINTS.SCAN_RECEIPT, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to analyze recipe');
      }

      const data = await response.json();
      setRecipeData({
        recipe_name: data.recipe_name || 'Untitled Recipe',
        instructions: data.instructions || '',
      });
      setScanResults(
        (data.ingredients || []).map((item: any, idx: number) => ({
          ...item,
          id: idx,
          selected: true,
        }))
      );
    } catch (err: any) {
      setScanError(err.message || 'Failed to analyze recipe');
      setScanPreview(null);
    } finally {
      setIsScanning(false);
    }
  };

  const toggleItem = (id: number) => {
    setScanResults(prev =>
      prev.map(item => (item.id === id ? { ...item, selected: !item.selected } : item))
    );
  };

  const handleSave = async () => {
    const selected = scanResults.filter(i => i.selected);
    if (selected.length === 0) {
      setScanError('Please select at least one ingredient');
      return;
    }

    setIsSaving(true);
    setScanError(null);

    try {
      const requestBody: any = {
        items: selected.map(item => ({
          name: item.name,
          quantity: -1,
          categoryIds: item.categoryIds || [],
        })),
        duplicate_action: 'use_existing',
        collection_id: groceryCollectionId,
      };

      const response = await fetch(API_ENDPOINTS.ADD_RECEIPT_TO_COLLECTION, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save ingredients');
      }

      if (recipeData) {
        try {
          await fetch(API_ENDPOINTS.CREATE_RECIPE, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              name: recipeData.recipe_name,
              instructions: recipeData.instructions,
              source: 'scan',
              ingredients: selected.map((item, idx) => ({
                name: item.name,
                quantity: item.quantity != null && item.quantity !== '' ? Number(item.quantity) : null,
                unit: item.unit || null,
                sort_order: idx,
              })),
            }),
          });
          window.dispatchEvent(new CustomEvent('recipes-updated'));
        } catch (err) {
          console.warn('Failed to create recipe:', err);
        }
      }

      onRecipeAdded();
      handleClose();
    } catch (err: any) {
      setScanError(err.message || 'Failed to save recipe');
    } finally {
      setIsSaving(false);
    }
  };

  const addIngredientTag = (value: string) => {
    const tag = value.trim();
    if (tag && !searchIngredients.includes(tag)) {
      setSearchIngredients(prev => [...prev, tag]);
    }
    setIngredientInput('');
  };

  const handleIngredientKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addIngredientTag(ingredientInput);
    } else if (e.key === 'Backspace' && !ingredientInput && searchIngredients.length > 0) {
      setSearchIngredients(prev => prev.slice(0, -1));
    }
  };

  const handleOnlineSearch = async () => {
    if (!searchQuery.trim() && searchIngredients.length === 0 && !onHandOnly) return;
    setIsSearching(true);
    setSearchError(null);
    setSearchResults([]);
    setHasSearched(true);

    try {
      const params = new URLSearchParams();
      if (searchQuery.trim()) params.append('q', searchQuery.trim());
      if (!onHandOnly && searchIngredients.length > 0) params.append('ingredients', searchIngredients.join(','));
      if (onHandOnly) params.append('on_hand', 'true');
      if (minRating > 0) params.append('min_rating', String(minRating));

      const resp = await fetch(`${API_ENDPOINTS.SEARCH_RECIPES_ONLINE}?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Search failed');
      setSearchResults(data.results || []);
    } catch (err: any) {
      setSearchError(err.message || 'Search failed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSaveOnlineRecipe = async (result: OnlineRecipeResult) => {
    setSavingRecipeId(result.id);
    setSearchError(null);
    try {
      if (result.is_in_library && result.library_recipe_id) {
        // Already in library — just checkout, no extra Spoonacular API call
        const checkoutResp = await fetch(API_ENDPOINTS.CHECKOUT_LIBRARY_RECIPE(result.library_recipe_id), {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!checkoutResp.ok) {
          const err = await checkoutResp.json();
          throw new Error(err.error || 'Failed to add recipe');
        }
      } else {
        // New recipe: save to library (backend fetches details from Spoonacular)
        const saveResp = await fetch(API_ENDPOINTS.SAVE_LIBRARY_RECIPE, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            spoonacular_id: result.id,
            title: result.title,
            source_url: result.source_url,
            servings: result.servings,
          }),
        });
        if (!saveResp.ok) {
          const err = await saveResp.json();
          throw new Error(err.error || 'Failed to save recipe');
        }
      }
      setSavedRecipeIds(prev => new Set(prev).add(result.id));
      setSearchResults(prev => prev.map(r =>
        r.id === result.id ? { ...r, is_in_library: true, is_checked_out: true } : r
      ));
      window.dispatchEvent(new CustomEvent('recipes-updated'));
    } catch (err: any) {
      setSearchError(err.message || 'Failed to save recipe');
    } finally {
      setSavingRecipeId(null);
    }
  };

  if (!isOpen) return null;

  const renderScanTab = () => {
    if (isScanning) {
      return (
        <div className="ars-scan-processing">
          {scanPreview && <img src={scanPreview} alt="Processing" className="ars-scan-preview-img" />}
          <div className="ars-scan-processing-status">
            <div className="ars-spinner" />
            <span>Analyzing recipe…</span>
          </div>
        </div>
      );
    }

    if (scanResults.length > 0 && recipeData) {
      const selectedCount = scanResults.filter(i => i.selected).length;
      return (
        <div className="ars-results">
          <div className="ars-recipe-header">
            <div className="ars-recipe-name">{recipeData.recipe_name}</div>
            {recipeData.instructions && (
              <div className="ars-recipe-instructions">{recipeData.instructions}</div>
            )}
          </div>

          <div className="ars-results-label">
            {selectedCount} of {scanResults.length} ingredients selected
          </div>

          <div className="ars-results-list">
            {scanResults.map(item => (
              <label key={item.id} className="ars-result-item">
                <input
                  type="checkbox"
                  checked={item.selected}
                  onChange={() => toggleItem(item.id)}
                  className="ars-result-checkbox"
                />
                <span className="ars-result-name">{item.name}</span>
                {item.quantity && (
                  <span className="ars-result-qty">
                    {item.quantity}{item.unit ? ` ${item.unit}` : ''}
                  </span>
                )}
              </label>
            ))}
          </div>

          {scanError && <div className="ars-error">{scanError}</div>}

          <div className="ars-actions">
            <button className="ars-btn-secondary" onClick={resetScanState} disabled={isSaving}>
              Start Over
            </button>
            <button
              className="ars-btn-primary"
              onClick={handleSave}
              disabled={isSaving || selectedCount === 0}
            >
              {isSaving ? 'Saving…' : `Add Recipe + ${selectedCount} Ingredient${selectedCount !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="ars-scan-upload">
        <button
          className="ars-camera-btn"
          onClick={() => scanInputRef.current?.click()}
          disabled={isScanning}
        >
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
            <circle cx="12" cy="13" r="4" />
          </svg>
          <span className="ars-camera-label">Take photo of recipe</span>
          <span className="ars-camera-hint">AI will extract ingredients and instructions</span>
        </button>
        <input
          ref={scanInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handlePhotoSelect}
          style={{ display: 'none' }}
        />
        {scanError && <div className="ars-error">{scanError}</div>}
      </div>
    );
  };

  const renderOnlineTab = () => (
    <div className="ars-online">
      {/* Search form */}
      <div className="ars-online-form">
        {/* Name */}
        <div className="ars-field">
          <label className="ars-field-label">Recipe name</label>
          <input
            className="ars-field-input"
            type="text"
            placeholder="e.g. Chicken Tikka Masala"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleOnlineSearch()}
          />
        </div>

        {/* Ingredients */}
        <div className="ars-field">
          <label className="ars-field-label">Ingredients</label>
          <div className={`ars-tag-box${onHandOnly ? ' ars-tag-box--disabled' : ''}`}>
            {!onHandOnly && searchIngredients.map(tag => (
              <span key={tag} className="ars-tag">
                {tag}
                <button
                  className="ars-tag-remove"
                  onClick={() => setSearchIngredients(prev => prev.filter(t => t !== tag))}
                  aria-label={`Remove ${tag}`}
                >×</button>
              </span>
            ))}
            {onHandOnly
              ? <span className="ars-tag-placeholder">Using your kitchen items</span>
              : <input
                  className="ars-tag-input"
                  type="text"
                  placeholder={searchIngredients.length === 0 ? 'Type ingredient, press Enter…' : ''}
                  value={ingredientInput}
                  onChange={e => setIngredientInput(e.target.value)}
                  onKeyDown={handleIngredientKeyDown}
                  onBlur={() => ingredientInput.trim() && addIngredientTag(ingredientInput)}
                />
            }
          </div>
        </div>

        {/* On-hand toggle */}
        <div className="ars-toggle-row">
          <div className="ars-toggle-info">
            <span className="ars-toggle-label">On hand only</span>
            <span className="ars-toggle-hint">Use items from your Kitchen</span>
          </div>
          <button
            className={`ars-toggle${onHandOnly ? ' ars-toggle--on' : ''}`}
            onClick={() => setOnHandOnly(v => !v)}
            role="switch"
            aria-checked={onHandOnly}
          >
            <span className="ars-toggle-knob" />
          </button>
        </div>

        {/* Rating */}
        <div className="ars-field">
          <label className="ars-field-label">Min rating</label>
          <div className="ars-chips">
            {([0, 3, 4] as const).map(r => (
              <button
                key={r}
                className={`ars-chip${minRating === r ? ' ars-chip--active' : ''}`}
                onClick={() => setMinRating(r)}
              >
                {r === 0 ? 'Any' : `${r}+ ★`}
              </button>
            ))}
          </div>
        </div>

        <button
          className="ars-search-btn"
          onClick={handleOnlineSearch}
          disabled={isSearching || (!searchQuery.trim() && searchIngredients.length === 0 && !onHandOnly)}
        >
          {isSearching
            ? <><span className="ars-spinner" /> Searching…</>
            : <>
                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                  <circle cx="11" cy="11" r="8" /><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35" />
                </svg>
                Search
              </>
          }
        </button>
      </div>

      {/* Error */}
      {searchError && <div className="ars-error">{searchError}</div>}

      {/* Results */}
      {searchResults.length > 0 && (
        <div className="ars-online-results">
          {searchResults.map(result => {
            const saved = savedRecipeIds.has(result.id) || !!result.is_checked_out;
            const saving = savingRecipeId === result.id;
            return (
              <div key={result.id} className="ars-online-result">
                {result.image && (
                  <img src={result.image} alt={result.title} className="ars-online-result-img" />
                )}
                <div className="ars-online-result-info">
                  <div className="ars-online-result-title">
                    {result.title}
                    {result.is_in_library && (
                      <span className="ars-online-result-library-badge" title="Already in Wandering Sock library">📚</span>
                    )}
                  </div>
                  <div className="ars-online-result-meta">
                    {result.ready_in_minutes && <span>{result.ready_in_minutes} min</span>}
                    {result.used_ingredient_count > 0 && (
                      <span className="ars-online-result-match">
                        {result.used_ingredient_count} matched
                        {result.missed_ingredient_count > 0 && `, ${result.missed_ingredient_count} missing`}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  className={`ars-online-add-btn${saved ? ' ars-online-add-btn--saved' : ''}`}
                  onClick={() => !saved && handleSaveOnlineRecipe(result)}
                  disabled={saving || saved}
                >
                  {saving
                    ? <span className="ars-spinner" />
                    : saved
                    ? <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                    : <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                  }
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state */}
      {hasSearched && !isSearching && searchResults.length === 0 && !searchError && (
        <div className="ars-online-empty">No recipes found. Try different terms.</div>
      )}
    </div>
  );

  const content = (
    <div className="ars-overlay" onClick={handleClose}>
      <div className="ars-sheet" onClick={e => e.stopPropagation()}>
        <div className="ars-handle" />
        <div className="ars-header">
          <h3 className="ars-title">Add Recipe</h3>
          <button className="ars-close" onClick={handleClose} aria-label="Close">
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="ars-tabs">
          <button
            className={`ars-tab ${activeTab === 'scan' ? 'active' : ''}`}
            onClick={() => { setActiveTab('scan'); resetScanState(); }}
          >
            <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
            Scan
          </button>
          <button
            className={`ars-tab ${activeTab === 'online' ? 'active' : ''}`}
            onClick={() => setActiveTab('online')}
          >
            <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <circle cx="11" cy="11" r="8" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35" />
            </svg>
            Online Search
          </button>
        </div>

        <div className="ars-content">
          {activeTab === 'scan' ? renderScanTab() : renderOnlineTab()}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
};

export default AddRecipeSheet;
